export type HealthLevel = 'thriving' | 'healthy' | 'fading' | 'neglected' | 'new'

export interface HealthScoreInput {
  lastContactedAt: string | null
  contactFrequency: string
  interactionCount: number
  /** Count of interactions in last 30 days vs previous 30 days: positive = increasing */
  interactionTrend: number
  /** Average quality of recent interactions (0-1 scale: poor=0.25, neutral=0.5, good=0.75, great=1) */
  averageQuality: number | null
  /** Fraction of reminders completed for this contact (0-1) */
  reminderCompletionRate: number | null
}

export interface HealthScore {
  score: number
  level: HealthLevel
  factors: {
    recency: number
    trend: number
    quality: number
    reminders: number
  }
}

const FREQUENCY_DAYS: Record<string, number> = {
  weekly: 7,
  biweekly: 14,
  monthly: 30,
  quarterly: 90,
  yearly: 365,
}

const QUALITY_MAP: Record<string, number> = {
  great: 1,
  good: 0.75,
  neutral: 0.5,
  poor: 0.25,
}

/**
 * Compute a 0-100 health score for a contact.
 * Returns { level: 'new' } for contacts with no interactions.
 */
export function computeHealthScore(input: HealthScoreInput): HealthScore {
  // Contacts with no interactions are "new"
  if (input.interactionCount === 0) {
    return {
      score: 0,
      level: 'new',
      factors: { recency: 0, trend: 0, quality: 0, reminders: 0 },
    }
  }

  // 1. Recency factor (40% weight)
  // How well the user is keeping up with their desired contact frequency
  let recency = 50 // Default if no frequency set
  const targetDays = FREQUENCY_DAYS[input.contactFrequency]
  if (targetDays && input.lastContactedAt) {
    const daysSince =
      (Date.now() - new Date(input.lastContactedAt).getTime()) / (1000 * 60 * 60 * 24)
    const ratio = daysSince / targetDays
    if (ratio <= 0.5) recency = 100
    else if (ratio <= 1.0)
      recency = 100 - (ratio - 0.5) * 40 // 100 → 80
    else if (ratio <= 1.5)
      recency = 80 - (ratio - 1.0) * 80 // 80 → 40
    else if (ratio <= 2.0)
      recency = 40 - (ratio - 1.5) * 60 // 40 → 10
    else recency = Math.max(0, 10 - (ratio - 2.0) * 10) // → 0
  } else if (!targetDays && input.lastContactedAt) {
    // No frequency set — just give a decent score based on recency
    const daysSince =
      (Date.now() - new Date(input.lastContactedAt).getTime()) / (1000 * 60 * 60 * 24)
    recency =
      daysSince <= 7
        ? 100
        : daysSince <= 30
          ? 80
          : daysSince <= 90
            ? 50
            : daysSince <= 180
              ? 25
              : 10
  }

  // 2. Trend factor (20% weight)
  // Positive trend = increasing interactions, negative = declining
  let trend = 50 // neutral baseline
  if (input.interactionTrend > 0) trend = Math.min(100, 50 + input.interactionTrend * 25)
  else if (input.interactionTrend < 0) trend = Math.max(0, 50 + input.interactionTrend * 25)

  // 3. Quality factor (20% weight)
  const quality = input.averageQuality !== null ? input.averageQuality * 100 : 50

  // 4. Reminder completion rate (20% weight)
  const reminders = input.reminderCompletionRate !== null ? input.reminderCompletionRate * 100 : 50

  const score = Math.round(recency * 0.4 + trend * 0.2 + quality * 0.2 + reminders * 0.2)

  return {
    score,
    level: scoreToLevel(score),
    factors: {
      recency: Math.round(recency),
      trend: Math.round(trend),
      quality: Math.round(quality),
      reminders: Math.round(reminders),
    },
  }
}

export function scoreToLevel(score: number): HealthLevel {
  if (score >= 80) return 'thriving'
  if (score >= 50) return 'healthy'
  if (score >= 25) return 'fading'
  return 'neglected'
}

export function qualityToNumber(quality: string | null): number | null {
  if (!quality) return null
  return QUALITY_MAP[quality] ?? null
}

export const HEALTH_COLORS: Record<HealthLevel, { bg: string; text: string; dot: string }> = {
  thriving: {
    bg: 'bg-chartreuse-soft',
    text: 'text-chartreuse-foreground dark:text-chartreuse',
    dot: 'bg-chartreuse',
  },
  healthy: {
    bg: 'bg-lilac-soft',
    text: 'text-lilac-foreground dark:text-lilac',
    dot: 'bg-lilac',
  },
  fading: {
    bg: 'bg-coral-soft',
    text: 'text-coral',
    dot: 'bg-coral/70',
  },
  neglected: {
    bg: 'bg-coral-soft',
    text: 'text-coral',
    dot: 'bg-coral',
  },
  new: {
    bg: 'bg-muted',
    text: 'text-muted-foreground',
    dot: 'bg-outline',
  },
}
